<script setup lang="ts">
import { ref, computed, watchEffect, watch } from "vue";
import { useI18n } from "vue-i18n";

import type { UserType, UserRegisterType } from "@/common/types/user";

import {
  baseCheckbox,
  baseInput,
  baseMultiselect,
  basePassword,
  baseSelect,
  baseTextarea,
  svgIcon,
} from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = defineProps<{
  data: UserType | null;
  isEdit?: boolean;
}>();

const DEFAULT_DATA = {
  login: "",
  name: "",
  email: "",
  type: "",
  status: "",
};

const payload = ref<UserRegisterType>({ ...DEFAULT_DATA });
const errors = ref<{ [key: string]: string }>({});

const isValid = ref(false);

const password = ref("");
const passwordConfirm = ref("");

const optionStatus = [
  { option: t("common.labels.active"), value: "ACTIVE" },
  { option: t("common.labels.inactive"), value: "INACTIVE" },
];

const optionType = [
  { option: t("common.labels.administrator"), value: "ADMIN" },
  { option: t("common.labels.manager"), value: "MANAGER" },
];

function validate() {
  Object.keys(payload.value).forEach((key) => {
    delete errors.value[key];

    if (!payload.value[key] && key !== "active")
      errors.value[key] = t("error.required");
  });

  isValid.value = Object.keys(errors.value).length === 0;
}

function validatePassword() {
  delete errors.value["password"];

  if (!password.value || !passwordConfirm.value)
    errors.value["password"] = t("error.required");

  if (password.value !== passwordConfirm.value)
    errors.value["password"] = t("error.not_equal");

  isValid.value = Object.keys(errors.value).length === 0;
}

function update() {
  validate();

  emit("update-data", payload.value);
}

function updatePassword() {
  validatePassword();

  payload.value = {
    ...payload.value,
    password: password.value,
  };

  emit("update-data", payload.value);
}

function resumeData() {
  if (!props.data) return;

  const { login, name, email, type, status } = props.data;

  payload.value = {
    ...payload.value,
    login,
    name,
    email,
    type,
    status,
  };

  if (props.data.password) {
    password.value = props.data.password;
    passwordConfirm.value = props.data.password;
  }
}

function valid() {
  validate();
  validatePassword();
}

defineExpose({ valid, isValid });

watch(
  () => props.data,
  () => {
    resumeData();
  },
  { immediate: true },
);
</script>

<template>
  <article class="form-user">
    <base-input
      v-model="payload.name"
      :value="payload.name"
      :placeholder="t('common.placeholders.name')"
      required
      maxlength="75"
      @blur="update()"
    >
      <template #label> {{ t("common.labels.name") }} *</template>
      <template v-if="errors.name" #error> {{ errors.name }} </template>
    </base-input>

    <section class="flex flex-row gap-4">
      <base-input
        v-model="payload.login"
        :value="payload.login"
        :placeholder="t('common.placeholders.login')"
        required
        maxlength="20"
        @blur="update()"
      >
        <template #label> {{ t("common.labels.login") }} *</template>
        <template v-if="errors.login" #error> {{ errors.login }} </template>
      </base-input>

      <base-input
        v-model="payload.email"
        :value="payload.email"
        :placeholder="t('common.placeholders.email')"
        required
        maxlength="75"
        @blur="update()"
      >
        <template #label> {{ t("common.labels.email") }}</template>
        <template v-if="errors.email" #error> {{ errors.email }} </template>
      </base-input>
    </section>

    <section class="flex flex-row gap-4">
      <base-select
        v-model="payload.type"
        :value="payload.type"
        :options="optionType"
        :is-empty="false"
        @change="update()"
      >
        <template #label> {{ t("common.labels.type") }}</template>
      </base-select>

      <base-select
        v-model="payload.status"
        :value="payload.status"
        :options="optionStatus"
        :is-empty="false"
        @change="update()"
      >
        <template #label> {{ t("common.labels.status") }}</template>
      </base-select>
    </section>

    <template v-if="!isEdit">
      <section class="flex flex-row gap-4">
        <base-password
          v-model="password"
          :value="password"
          :placeholder="t('user.placeholders.password')"
          minlength="8"
          maxlength="25"
          @change="updatePassword()"
        >
          <template #label> {{ t("user.labels.password") }} *</template>
          <template v-if="errors.password" #error>
            {{ errors.password }}
          </template>
        </base-password>

        <base-password
          v-model="passwordConfirm"
          :value="passwordConfirm"
          :placeholder="t('user.placeholders.confirm_password')"
          minlength="8"
          maxlength="25"
          @change="updatePassword()"
        >
          <template #label> {{ t("user.labels.confirm_password") }} *</template>
          <template v-if="errors.password" #error>
            {{ errors.password }}
          </template>
        </base-password>
      </section>
    </template>
  </article>
</template>

<style lang="scss" scoped>
.form-user {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
}
</style>
